import { lazyLoadView } from '@utils/LazyLoad'
import { FEATURES, MODAL, SEARCH_GROUPS } from '@constants/Text'

import { checkPermission as commissionsCheckPermission } from '@modules/comissoes/routes'
import { podeAdministrarCatalogo } from '@modules/solicitacoes/utils/escritorioInterno'
import { podeAcessarBaseAtivos } from '@modules/settings/utils/base-ativos-consultor'
import { podeAcessarConexoesAtendimento } from '@modules/settings/utils/atendimento-conexoes-acesso'
import { ASSETS_ROUTE_NAME } from '@modules/settings/constants/Text'

export const permissionSettingsV2 = (
  { user, show },
  gestor,
  additionalPermission
) => {
  const settingsValidation = true

  if (gestor && additionalPermission && additionalPermission.length) {
    const validPermissions = additionalPermission.some(
      (permission) => show[permission]
    )
    return settingsValidation && user.IS_GESTOR && validPermissions
  }

  if (gestor) {
    return settingsValidation && user.IS_GESTOR
  }

  if (additionalPermission) {
    return (
      settingsValidation &&
      additionalPermission.some((permission) => show[permission])
    )
  }

  return settingsValidation
}

// Guard de rota que exige IS_GESTOR sem checar features.
// Existe para ser referenciado direto em `meta.customPermission`,
// evitando o bug de passar `permissionSettingsV2` cru (que com 1 arg sempre retorna true).
export const requireGestor = (user) => permissionSettingsV2(user, true)

// Fábrica de guard de rota que exige IS_GESTOR + ao menos uma das features informadas.
// Centraliza o padrão repetido em dezenas de rotas de settings, eliminando duplicação
// detectada pelo SonarQube e mantendo um único ponto de verdade da regra de permissão.
export const requireFeatures =
  (...features) =>
  (ctx) =>
    permissionSettingsV2(ctx, true, features)

// Guard da Base de Ativos (Configurações > Asset Allocation > Ativos).
//
// Única exceção à política gestor-only das Configurações: o consultor entra quando o
// escritório habilita `BASE_ATIVOS_CONSULTOR`. A flag é opt-in — sem registro no banco o
// resultado é idêntico a `requireFeatures(ASSET_LOCATION)`, ou seja, o comportamento atual.
//
// ★ ASSET_LOCATION continua obrigatório para todos. A flag do consultor ELEVA permissão
// dentro do módulo; não substitui o acesso ao módulo. Sem esse `&&`, um escritório sem
// Asset Allocation que ligasse a flag abriria uma tela que não deveria nem existir pra ele.
//
// Vale só para o módulo, sua home e a tela de Ativos — as demais filhas (perfil,
// benchmarks, carteiras, permissões, ...) seguem com `requireFeatures` e portanto
// gestor-only. Os cards dessas telas são escondidos do consultor na home do módulo.
//
// ★ A regra mora em `@modules/settings/utils/base-ativos-consultor` — cinco consumidores a
// reescreviam. O util também reprova o CLIENTE FINAL, que a versão anterior deixava passar:
// ela só somava capacidade (`IS_GESTOR || flag`) e nunca subtraía perfil.
export const requireBaseAtivos = ({ user, show }) =>
  podeAcessarBaseAtivos(user, show)

// Guard da sub-árvore de Atendimento (Configurações > Mensagens > Conexões).
//
// Segunda exceção à política gestor-only das Configurações, pelo mesmo motivo estrutural da
// Base de Ativos: a regra REAL destas telas nunca foi "é gestor", e sim "tem a feature e é
// profissional". A conexão de WhatsApp é do PROFISSIONAL — ele lê o QR Code no próprio
// celular, reconecta quando cai e desconecta ao trocar de aparelho. Exigir IS_GESTOR obrigava
// o gestor a operar o celular alheio, ou o escritório a promover a equipe inteira a gestor.
//
// ★ FEATURES.ATENDIMENTO continua obrigatório para todos — não é liberação incondicional; o
// que cai é só o `true` de `permissionSettingsV2` que forçava `user.IS_GESTOR`. Sem a feature,
// gestor e não-gestor seguem barrados exatamente como antes.
//
// ★ A regra mora em `@modules/settings/utils/atendimento-conexoes-acesso` porque o menu de
// ações da conexão precisa responder a MESMA pergunta: menu mais frouxo que o guard vira link
// para 404; mais apertado, funcionalidade inalcançável. O util também reprova o CLIENTE FINAL
// (estas rotas não declaram `meta.permission`, então o guard global cairia no `else { next() }`).
export const requireConexoesAtendimento = ({ user, show }) =>
  podeAcessarConexoesAtendimento(user, show)

// Guard das Configurações de Solicitações: flag + gestor (requireFeatures) +
// escritório que ADMINISTRA o catálogo (`podeAdministrarCatalogo`: interno 2976
// + escritório de teste 83).
//
// Usa `podeAdministrarCatalogo`, e NÃO `ehEscritorioInternoAawz`: as 4 abas
// desta tela são parametrização do catálogo COMPARTILHADO (conexões por tipo,
// jornada por etapa, catálogo Backoffice) — nenhuma lê dado de outro
// escritório. A Operação AAWZ e o modo RESPONDER da Comunicação, que leem a
// fila cross-escritório, seguem no helper estrito.
export const requireSolicitacoesInternal = (ctx) => {
  if (!requireFeatures(FEATURES.SOLICITACOES)(ctx)) return false
  return podeAdministrarCatalogo(ctx.user)
}

export default [
  {
    path: '/repositorio-arquivos/:id?',
    name: 'repositorio-arquivos',
    redirect: '/repositorio-arquivos/',
    meta: {
      permission: ['gestor'],
      can_show: FEATURES.REPOSITORY_FILES,
    },
    component: () =>
      lazyLoadView(
        import(
          '@modules/settings/views/repositorios/view-repositorios-arquivos.vue'
        )
      ),
    children: [
      {
        path: '',
        name: 'repositorio-arquivos.home',
        meta: {
          permission: ['gestor'],
          can_show: FEATURES.REPOSITORY_FILES,
          search: {
            title: 'Repositório de Arquivos',
            description: 'Repositório de arquivos e documentos do escritório',
            keywords: ['repositório', 'arquivos', 'documentos', 'files'],
            icon: 'mdi-package',
            group: SEARCH_GROUPS.GESTAO_ESCRITORIOS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/repositorios/view-repositorios-arquivos.vue'
            )
          ),
      },
    ],
  },

  {
    path: '/settings',
    name: 'settings',
    meta: {
      customPermission: requireGestor,
    },
    redirect: '/settings/',
    component: () => lazyLoadView(import('@views/settings/index')),
    children: [
      {
        path: '',
        name: 'settings.home',
        meta: {
          customPermission: requireGestor,
        },
        component: () => lazyLoadView(import('@views/settings/home')),
      },

      {
        path: 'assets',
        name: 'settings.assets',
        meta: {
          whitelabel_only: true,
          customPermission: requireGestor,
        },
        component: () => lazyLoadView(import('@views/settings/ativos/mercado')),
      },
      {
        path: 'ips',
        name: 'settings.ips',
        meta: {
          whitelabel_only: true,
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(import('@views/settings/ips/configurar-perguntas')),
      },
      {
        path: 'classes',
        name: 'settings.classes',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(import('@views/settings/classes-estrategicas')),
      },
      {
        path: 'anbima/classes',
        name: 'settings.anbima.classes',
        meta: {
          customPermission: requireGestor,
        },
        redirect: { name: 'settings.anbima.mapping' },
      },
      {
        path: 'anbima/class-mapping',
        name: 'settings.anbima.mapping',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(import('@views/settings/anbima/class-mapping.vue')),
      },
      {
        path: 'email',
        name: 'settings.email',
        meta: {
          customPermission: requireGestor,
        },
        component: () => lazyLoadView(import('@views/settings/email/index')),
      },
      {
        path: 'portfolio',
        name: 'settings.portfolio',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(import('@views/settings/portfolio-modelo/index')),
      },
      {
        path: 'benchmark',
        name: 'settings.benchmark',
        meta: {
          whitelabel_only: true,
          customPermission: requireGestor,
        },
        component: () => lazyLoadView(import('@views/settings/set-benchmark')),
      },
      {
        path: 'carteiras',
        name: 'settings.carteiras',
        meta: {
          whitelabel_only: true,
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import('@views/settings/carteiras/carteiras-recomendadas')
          ),
      },
      {
        path: 'clientes',
        name: 'settings.clientes',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(import('@views/settings/clientes/configurar-clientes')),
      },
      {
        path: 'grupo-familiar',
        name: 'settings.grupo-familiar',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import('@modules/settings/views/family-group/view-family-group.vue')
          ),
      },
      {
        path: 'modelo',
        name: 'settings.pdf_modelo',
        meta: {
          whitelabel_only: true,
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(import('@views/settings/pdf-modelo/index')),
      },
      {
        path: 'planilhas',
        name: 'settings.planilhas',
        meta: {
          whitelabel_only: true,
          can_show: FEATURES.BTG_WORKSHEETS,
          permission: ['gestor'],
          customPermission: requireFeatures(FEATURES.BTG_WORKSHEETS),
        },
        component: () =>
          lazyLoadView(import('@views/settings/planilhas/gerenciar-planilhas')),
      },
      {
        path: 'planilhas_xp',
        name: 'settings.xp_sheets',
        meta: {
          whitelabel_only: true,
          can_show: FEATURES.XP_WORKSHEETS,
          customPermission: requireFeatures(FEATURES.XP_WORKSHEETS),
        },
        component: () =>
          lazyLoadView(import('@modules/settings/views/view-xp-sheets')),
      },
      {
        path: 'extrato_caixa',
        name: 'settings.extract_cash_sheets',
        meta: {
          whitelabel_only: true,
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import('@modules/settings/views/extract-cash/view-extract-cash')
          ),
      },
      {
        path: 'plano_carreira',
        name: 'settings.plano_carreira',
        meta: {
          whitelabel_only: true,
          can_show: FEATURES.PARTNERSHIP,
          customPermission: requireFeatures(FEATURES.PARTNERSHIP),
        },
        component: () =>
          lazyLoadView(
            import('@views/settings/partnership/settings-career-path')
          ),
      },
      {
        path: 'empresas',
        name: 'settings.empresas',
        meta: {
          whitelabel_only: true,
          // can_show: FEATURES.PARTNERSHIP,
          listCanShow: [FEATURES.PARTNERSHIP, FEATURES.BPO_FINANCE],
          customPermission: requireFeatures(
            FEATURES.PARTNERSHIP,
            FEATURES.BPO_FINANCE
          ),
        },
        component: () =>
          lazyLoadView(
            import('@views/settings/partnership/settings-companies')
          ),
      },
      {
        path: 'metricas',
        name: 'settings.metricas',
        meta: {
          whitelabel_only: true,
          // can_show: FEATURES.BPO_FINANCE,

          listCanShow: [FEATURES.PARTNERSHIP, FEATURES.BPO_FINANCE],
          customPermission: requireFeatures(
            FEATURES.PARTNERSHIP,
            FEATURES.BPO_FINANCE
          ),
        },
        component: () =>
          lazyLoadView(
            import('@views/settings/partnership/settings-metrics-view')
          ),
      },
      {
        path: 'modelo_cobranca',
        name: 'settings.fee_model',
        meta: {
          whitelabel_only: true,
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(import('@views/settings/fee-model/manage-fee-models')),
      },
      {
        path: 'bonificacao',
        name: 'settings.bonus',
        meta: {
          whitelabel_only: true,
          can_show: FEATURES.BONIFICATION,
          customPermission: requireFeatures(FEATURES.BONIFICATION),
        },
        component: () =>
          lazyLoadView(import('@modules/settings/views/bonus/bonus')),
      },
      {
        path: 'parametros_escritorio',
        name: 'settings.parameters_office',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/paramenters-office/view-parameters-office'
            )
          ),
      },
      {
        path: 'permissoes',
        name: 'settings.permissionsBI',
        meta: {
          can_show: FEATURES.BI_CUSTOMIZED,
          permission: ['gestor'],
          customPermission: requireFeatures(FEATURES.BI_CUSTOMIZED),
        },
        component: () =>
          lazyLoadView(
            import('@modules/power-bi/views/permissions/view-permissions')
          ),
      },
      {
        path: 'acesso-assessores',
        name: 'settings.permissionsAssessor',
        meta: {
          can_show: 'settings',
          permission: ['gestor'],
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import('@modules/settings/views/view-permissions-assessor')
          ),
      },
    ],
  },

  // TODO: remove older routes
  {
    path: '/configuracoes',
    name: 'settings-v2',
    meta: {
      customPermission: requireGestor,
      search: {
        title: 'Configurações',
        description: 'Configurações gerais e por módulo do escritório',
        keywords: ['configurações', 'ajustes', 'settings', 'preferências'],
        icon: 'setting',
        group: SEARCH_GROUPS.CONFIGURACOES,
      },
    },
    redirect: '/configuracoes/',
    component: () =>
      lazyLoadView(
        import('@modules/settings/views/view-base-home-settings.vue')
      ),
    children: [
      {
        path: '',
        name: 'settings-v2.home',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(import('@modules/settings/views/view-home-v2.vue')),
      },
      {
        path: '/configuracoes/bi',
        name: 'settings-v2.bi',
        meta: {
          customPermission: requireFeatures(
            FEATURES.BI_CUSTOMIZED,
            FEATURES.BI_V2
          ),
          search: {
            title: 'Configurações — BI',
            description: 'Configurações do BI, segmentação e permissões',
            keywords: ['configurações', 'bi', 'segmentação', 'permissões'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        redirect: '/configuracoes/bi/',
        component: () =>
          lazyLoadView(
            import('@modules/settings/views/bi-settings/view-bi-settings.vue')
          ),
        children: [
          {
            path: '/',
            name: 'settings-v2.bi.home',
            meta: {
              customPermission: requireFeatures(
                FEATURES.BI_CUSTOMIZED,
                FEATURES.BI_V2
              ),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/bi-home-settings/view-bi-home-settings.vue'
                )
              ),
          },
          {
            path: 'segmentacao',
            name: 'settings-v2.bi.segmentation_customers',
            meta: {
              customPermission: requireFeatures(
                FEATURES.BI_CUSTOMIZED,
                FEATURES.BI_V2
              ),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/customer-segmentation/view-customer-segmentation.vue'
                )
              ),
          },
          {
            path: 'permissoes',
            name: 'settings-v2.bi.permissoes',
            meta: {
              customPermission: requireFeatures(
                FEATURES.BI_CUSTOMIZED,
                FEATURES.BI_V2
              ),
            },
            component: () =>
              lazyLoadView(
                import('@modules/settings/views/view-bi-permissions.vue')
              ),
          },
          {
            path: 'segmentacao/faixa-pl',
            name: 'settings-v2.bi.segmentation_customers.pl',
            meta: {
              customPermission: requireFeatures(
                FEATURES.BI_CUSTOMIZED,
                FEATURES.BI_V2
              ),
            },
            component: () =>
              lazyLoadView(
                import('@views/settings/clientes/configurar-clientes.vue')
              ),
          },
          {
            path: 'segmentacao/grupo-familiar',
            name: 'settings-v2.bi.segmentation_customers.family_group',
            meta: {
              customPermission: requireFeatures(
                FEATURES.BI_CUSTOMIZED,
                FEATURES.BI_V2
              ),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/family-group/view-family-group.vue'
                )
              ),
          },
          {
            path: 'equipes',
            name: 'settings-v2.bi.teams',
            meta: {
              customPermission: requireFeatures(
                FEATURES.BI_CUSTOMIZED,
                FEATURES.BI_V2
              ),
            },
            component: () =>
              lazyLoadView(
                import('@modules/settings/views/teams/view-teams.vue')
              ),
          },
          {
            path: 'super-ranking',
            name: 'settings-v2.bi.super_ranking',
            redirect: 'super-ranking/',
            meta: {
              customPermission: requireFeatures(
                FEATURES.BI_CUSTOMIZED,
                FEATURES.BI_V2
              ),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/super-ranking/view-super-ranking.vue'
                )
              ),
            children: [
              {
                path: '',
                name: 'settings-v2.bi.super_ranking.overview',
                meta: {
                  customPermission: requireFeatures(
                    FEATURES.BI_CUSTOMIZED,
                    FEATURES.BI_V2
                  ),
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/settings/views/super-ranking/tabs/tab-overview.vue'
                    )
                  ),
              },
              {
                path: 'ciclos',
                name: 'settings-v2.bi.super_ranking.cycles',
                meta: {
                  customPermission: requireFeatures(
                    FEATURES.BI_CUSTOMIZED,
                    FEATURES.BI_V2
                  ),
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/settings/views/super-ranking/tabs/tab-cycles.vue'
                    )
                  ),
              },
              {
                path: 'clusters',
                name: 'settings-v2.bi.super_ranking.clusters',
                meta: {
                  customPermission: requireFeatures(
                    FEATURES.BI_CUSTOMIZED,
                    FEATURES.BI_V2
                  ),
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/settings/views/super-ranking/tabs/tab-clusters.vue'
                    )
                  ),
              },
              {
                path: 'indicadores',
                name: 'settings-v2.bi.super_ranking.indicators',
                meta: {
                  customPermission: requireFeatures(
                    FEATURES.BI_CUSTOMIZED,
                    FEATURES.BI_V2
                  ),
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/settings/views/super-ranking/tabs/tab-indicators.vue'
                    )
                  ),
              },
              {
                path: 'metas',
                name: 'settings-v2.bi.super_ranking.goals',
                meta: {
                  customPermission: requireFeatures(
                    FEATURES.BI_CUSTOMIZED,
                    FEATURES.BI_V2
                  ),
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/settings/views/super-ranking/tabs/tab-goals.vue'
                    )
                  ),
              },
              {
                path: 'participantes',
                name: 'settings-v2.bi.super_ranking.participants',
                meta: {
                  customPermission: requireFeatures(
                    FEATURES.BI_CUSTOMIZED,
                    FEATURES.BI_V2
                  ),
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/settings/views/super-ranking/tabs/tab-participants.vue'
                    )
                  ),
              },
              {
                path: 'resultado',
                name: 'settings-v2.bi.super_ranking.results',
                meta: {
                  customPermission: requireFeatures(
                    FEATURES.BI_CUSTOMIZED,
                    FEATURES.BI_V2
                  ),
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/settings/views/super-ranking/tabs/tab-calculate.vue'
                    )
                  ),
              },
            ],
          },
        ],
      },
      {
        path: '/configuracoes/financeiro',
        name: 'settings-v2.finance',
        meta: {
          customPermission: requireFeatures(FEATURES.BPO_FINANCE),
          search: {
            title: 'Configurações — Financeiro',
            description: 'Configurações do módulo financeiro e DRE',
            keywords: ['configurações', 'financeiro', 'dre', 'planos'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        redirect: '/configuracoes/financeiro/',
        component: () =>
          lazyLoadView(
            import('@modules/settings/views/finance-settings/view-finance.vue')
          ),
        children: [
          {
            path: '/',
            name: 'settings-v2.finance.home',
            meta: {
              customPermission: requireFeatures(FEATURES.BPO_FINANCE),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/finance-settings-home/view-finance-home.vue'
                )
              ),
          },
          {
            path: 'equipes',
            name: 'settings-v2.finance.teams',
            meta: {
              customPermission: requireFeatures(FEATURES.BPO_FINANCE),
            },
            component: () =>
              lazyLoadView(
                import('@modules/settings/views/teams/view-teams.vue')
              ),
          },
          {
            path: 'planos',
            name: 'settings-v2.finance.plans',
            meta: {
              customPermission: requireFeatures(FEATURES.BPO_FINANCE),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/plans-accounts/view-plans-accounts.vue'
                )
              ),
          },
          {
            path: 'produtos',
            name: 'settings-v2.finance.products',
            meta: {
              customPermission: requireFeatures(FEATURES.BPO_FINANCE),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/area-products/view-area-products.vue'
                )
              ),
          },
          {
            path: 'categorias-comissao',
            name: 'settings-v2.finance.categories.commission',
            meta: {
              customPermission: requireFeatures(FEATURES.BPO_FINANCE),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/categories-commission/view-categories-commission.vue'
                )
              ),
          },

          {
            path: 'departamentos',
            name: 'settings-v2.finance.departments',
            meta: {
              customPermission: requireFeatures(FEATURES.BPO_FINANCE),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/departments/view-departments-settings.vue'
                )
              ),
          },
          {
            path: 'metricas',
            name: 'settings-v2.finance.metrics',
            meta: {
              customPermission: requireFeatures(FEATURES.BPO_FINANCE),
            },
            component: () =>
              lazyLoadView(
                import('@modules/settings/views/metrics/view-metrics.vue')
              ),
          },
          {
            path: 'versoes-dre',
            name: 'settings-v2.finance.dre-versions',
            redirect: '/configuracoes/financeiro/versoes-dre/dres/geral',
            meta: {
              customPermission: requireFeatures(FEATURES.BPO_FINANCE),
            },
            component: () =>
              lazyLoadView(
                import('@modules/settings/views/dre-nav-bar/dre-nav-bar.vue')
              ),
            children: [
              {
                path: 'dres',
                name: 'settings-v2.finance.dre.group',
                redirect: '/configuracoes/financeiro/versoes-dre/dres/geral',
                meta: {
                  customPermission: requireFeatures(FEATURES.BPO_FINANCE),
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/settings/views/dre-options-nav-bar/dre-options-nav-bar.vue'
                    )
                  ),
                children: [
                  {
                    path: ':type',
                    name: 'settings-v2.finance.dre.group.type',
                    meta: {
                      customPermission: requireFeatures(FEATURES.BPO_FINANCE),
                    },
                    component: () =>
                      lazyLoadView(
                        import(
                          '@modules/settings/views/dre-temp/view-dre-temp.vue'
                        )
                      ),
                  },
                ],
              },
              {
                path: 'comparativo',
                name: 'settings-v2.finance.dre.comparative',
                redirect:
                  '/configuracoes/financeiro/versoes-dre/comparativo/geral',
                meta: {
                  customPermission: requireFeatures(FEATURES.BPO_FINANCE),
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/settings/views/comparative-nav-bar/comparative-nav-bar.vue'
                    )
                  ),
                children: [
                  {
                    path: ':type',
                    name: 'settings-v2.finance.dre.comparative.type',
                    meta: {
                      customPermission: requireFeatures(FEATURES.BPO_FINANCE),
                    },
                    component: () =>
                      lazyLoadView(
                        import(
                          '@modules/settings/views/dre-comparative/view-dre-comparative.vue'
                        )
                      ),
                  },
                ],
              },
              {
                path: 'versoes',
                name: 'settings-v2.finance.dre.versions',
                meta: {
                  customPermission: requireFeatures(FEATURES.BPO_FINANCE),
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/settings/views/dre-versions/view-dre-versions.vue'
                    )
                  ),
              },
            ],
          },
        ],
      },

      {
        path: '/configuracoes/juridico',
        name: 'settings-v2.juridico',
        redirect: '/configuracoes/juridico/',
        meta: {
          customPermission: requireFeatures(FEATURES.CAPTABLE),
          search: {
            title: 'Configurações — Partnership',
            description:
              'Configurações de partnership e documentos societários',
            keywords: [
              'configurações',
              'partnership',
              'jurídico',
              'societário',
            ],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@/src/modules/settings/views/juridico/view-juridico/juridico-navigation.vue'
            )
          ),
        children: [
          {
            path: '/',
            name: 'settings-v2.juridico.home',
            meta: {
              customPermission: requireFeatures(FEATURES.CAPTABLE),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/juridico/view-modulo-juridico.vue'
                )
              ),
          },
          {
            path: 'kit-societario',
            name: 'settings-v2.juridico.kit-partners',
            meta: {
              customPermission: requireFeatures(FEATURES.CAPTABLE),
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/juridico/settings-kit-partners/settings-kit-partners.vue'
                )
              ),
          },

          {
            path: 'configuracoes/juridico/profissionais',
            name: 'settings-v2.juridico.profissionais',
            meta: {
              customPermission: requireFeatures(FEATURES.CAPTABLE),
              search: {
                title: 'Configurações — Partnership — Profissionais',
                description: 'Cadastro de profissionais do módulo Partnership',
                keywords: [
                  'partnership',
                  'jurídico',
                  'profissionais',
                  'sócios',
                ],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/juridico/view-juridico-profissionais.vue'
                )
              ),
          },
          {
            path: 'configuracoes/juridico/tipos',
            name: 'settings-v2.juridico.tipos',
            meta: {
              customPermission: requireFeatures(FEATURES.CAPTABLE),
              search: {
                title: 'Configurações — Partnership — Tipos de Documentos',
                description: 'Tipos de documentos societários do Partnership',
                keywords: ['partnership', 'jurídico', 'tipos', 'documentos'],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/type-documents/view-type-documents.vue'
                )
              ),
          },
        ],
      },

      // ESTRATEGIA

      {
        path: '/configuracoes/estrategia',
        name: 'settings-v2.estrategia',
        meta: {
          customPermission: requireFeatures(FEATURES.ESTRATEGIA),
          search: {
            title: 'Configurações — Estratégia e Performance',
            description: 'Configurações de estratégia, carreira e equipes',
            keywords: [
              'configurações',
              'estratégia',
              'performance',
              'carreira',
            ],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        redirect: '/configuracoes/estrategia',
        component: () =>
          lazyLoadView(
            import(
              '@/src/modules/settings/views/estrategia/view-modulo-estrategia-perfomance.vue'
            )
          ),
        children: [
          {
            path: '/',
            name: 'settings-v2.estrategia.home',
            meta: {
              customPermission: requireFeatures(FEATURES.ESTRATEGIA),
            },

            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/estrategia/view-estrategia-home.vue'
                )
              ),
          },
          {
            path: 'equipes',
            name: 'settings-v2.estrategia.teams',
            meta: {
              customPermission: requireFeatures(FEATURES.ESTRATEGIA),
              search: {
                title: 'Configurações — Estratégia e Performance — Equipes',
                description: 'Equipes usadas em Estratégia e Performance',
                keywords: ['estratégia', 'performance', 'equipes', 'times'],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/estrategia/view-teams-estrategia.vue'
                )
              ),
          },
          {
            path: 'carreiras',
            name: 'settings-v2.estrategia.careers',
            meta: {
              customPermission: requireFeatures(FEATURES.ESTRATEGIA),
              search: {
                title:
                  'Configurações — Estratégia e Performance — Plano de Carreira',
                description: 'Configurações e estrutura do plano de carreira',
                keywords: ['estratégia', 'carreira', 'plano de carreira'],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () => {
              return lazyLoadView(
                import(
                  '@/src/modules/settings/views/estrategia/view-career-path-estrategia.vue'
                )
              )
            },
          },
          {
            path: 'carreiras/configuracoes',
            name: 'settings-v2.estrategia.careers.configuracoes',
            meta: {
              customPermission: requireFeatures(FEATURES.ESTRATEGIA),
              search: {
                title:
                  'Configurações — Estratégia e Performance — Configurações do Plano de Carreira',
                description:
                  'Níveis de senioridade e segmentações do plano de carreira',
                keywords: ['carreira', 'senioridade', 'níveis', 'segmentação'],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () => {
              return lazyLoadView(
                import(
                  '@/src/modules/settings/views/estrategia/view-career-path-configuracoes.vue'
                )
              )
            },
          },
          {
            path: 'carreiras/estrutura',
            name: 'settings-v2.estrategia.careers.estrutura',
            meta: {
              customPermission: requireFeatures(FEATURES.ESTRATEGIA),
              search: {
                title:
                  'Configurações — Estratégia e Performance — Estrutura do Plano de Carreira',
                description: 'Estrutura do plano de carreira da empresa',
                keywords: ['carreira', 'estrutura', 'organograma'],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () => {
              return lazyLoadView(
                import(
                  '@/src/modules/settings/views/estrategia/view-career-path-estrutura.vue'
                )
              )
            },
          },

          {
            path: 'segmentacao',
            name: 'settings-v2.estrategia.segmentation_customers',
            meta: {
              customPermission: requireFeatures(FEATURES.ESTRATEGIA),
              search: {
                title:
                  'Configurações — Estratégia e Performance — Segmentação de Clientes',
                description: 'Faixas de P/L e grupos familiares dos clientes',
                keywords: [
                  'segmentação',
                  'clientes',
                  'faixa',
                  'grupo familiar',
                ],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/estrategia/view-customer-segmentation-estrategia.vue'
                )
              ),
          },
          {
            path: 'segmentacao/faixa-pl',
            name: 'settings-v2.estrategia.segmentation_customers.pl',
            meta: {
              customPermission: requireFeatures(FEATURES.ESTRATEGIA),
              search: {
                title:
                  'Configurações — Estratégia e Performance — Faixas de P/L',
                description: 'Faixas de P/L por valor mínimo e máximo',
                keywords: ['p/l', 'pl', 'patrimônio', 'faixa', 'segmentação'],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/estrategia/view-faixa-pl-estrategia.vue'
                )
              ),
          },
          {
            path: 'segmentacao/grupo-familiar',
            name: 'settings-v2.estrategia.segmentation_customers.family_group',
            meta: {
              customPermission: requireFeatures(FEATURES.ESTRATEGIA),
              search: {
                title:
                  'Configurações — Estratégia e Performance — Grupo Familiar',
                description: 'Cadastro de grupos familiares de clientes',
                keywords: ['grupo familiar', 'família', 'segmentação'],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/estrategia/view-family-group-estrategia.vue'
                )
              ),
          },
        ],
      },

      {
        path: '/configuracoes/crm',
        name: 'settings-v2.crm',
        meta: {
          customPermission: requireFeatures(FEATURES.CRM),
          search: {
            title: 'Configurações — CRM',
            description: 'Configurações do CRM, funil e campos personalizados',
            keywords: ['configurações', 'crm', 'funil', 'relacionamento'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        redirect: '/configuracoes/crm/',
        component: () =>
          lazyLoadView(
            import('@modules/settings/views/crm/view-crm-settings.vue')
          ),
        children: [
          {
            path: '/',
            name: 'settings-v2.crm.home',
            meta: {
              customPermission: requireFeatures(FEATURES.CRM),
              pageTitle: 'CRM',
            },
            component: () =>
              lazyLoadView(
                import('@modules/settings/views/crm/view-crm-home.vue')
              ),
          },
          {
            path: 'regua-relacionamento',
            name: 'settings-v2.crm.regua-relacionamento',
            meta: {
              customPermission: requireFeatures(FEATURES.CRM),
              pageTitle: 'Régua de relacionamento',
              search: {
                title: 'Configurações — CRM — Régua de relacionamento',
                description:
                  'Cadastro e sincronização da régua de relacionamento com clientes',
                keywords: [
                  'régua',
                  'régua de relacionamento',
                  'cadastrar régua',
                  'criar régua',
                  'adicionar régua',
                  'sincronizar régua',
                  'sincronizar régua dos clientes',
                  'sincronização',
                ],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/crm/view-crm-regua-relacionamento/view-crm-regua-relacionamento.vue'
                )
              ),
          },
          {
            path: 'funil',
            name: 'settings-v2.crm.funil',
            meta: {
              customPermission: requireFeatures(FEATURES.CRM),
              pageTitle: 'Funil',
              search: {
                title: 'Configurações — CRM — Funil',
                description: 'Etapas e critérios do funil de leads',
                keywords: [
                  'funil',
                  'cadastrar funil',
                  'criar funil',
                  'adicionar funil',
                  'etapas do funil',
                  'configurar funil',
                  'pipeline de leads',
                ],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/crm/view-crm-funil/view-crm-funil.vue'
                )
              ),
          },
          {
            // Antiga 'campos-personalizados' — renomeada e ampliada pra
            // agrupar as 3 frentes de personalização do detalhe de
            // Lead/Cliente (painel lateral, Visão Geral, campos
            // personalizados), uma rota por aba (padrão de
            // compliance.ips.* — BaseSegmentedTabs amarrado a $route.name +
            // router-view, sem estado local de aba). Ver
            // view-crm-personalizacao.vue (shell) e settings/CLAUDE.md §14.18.
            path: 'personalizacao',
            name: 'settings-v2.crm.personalizacao',
            redirect: '/configuracoes/crm/personalizacao/painel',
            meta: {
              customPermission: requireFeatures(FEATURES.CRM),
              pageTitle: 'Personalização',
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/crm/view-crm-personalizacao/view-crm-personalizacao.vue'
                )
              ),
            children: [
              {
                path: 'painel',
                name: 'settings-v2.crm.personalizacao.painel',
                meta: {
                  customPermission: requireFeatures(FEATURES.CRM),
                  pageTitle: 'Painel do Lead/Cliente',
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@/src/modules/settings/views/crm/view-crm-personalizacao/view-crm-personalizacao-painel.vue'
                    )
                  ),
              },
              {
                path: 'visao-geral',
                name: 'settings-v2.crm.personalizacao.visao-geral',
                meta: {
                  customPermission: requireFeatures(FEATURES.CRM),
                  pageTitle: 'Visão Geral',
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@/src/modules/settings/views/crm/view-crm-personalizacao/view-crm-personalizacao-visao-geral.vue'
                    )
                  ),
              },
              {
                path: 'campos-personalizados',
                name: 'settings-v2.crm.personalizacao.campos',
                meta: {
                  customPermission: requireFeatures(FEATURES.CRM),
                  pageTitle: 'Campos personalizados',
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@/src/modules/settings/views/crm/view-crm-custom-fields/view-crm-custom-fields.vue'
                    )
                  ),
              },
            ],
          },
          {
            // Configuração de Oportunidades (gestor-only via requireFeatures).
            path: 'oportunidades',
            name: 'settings-v2.crm.oportunidades',
            meta: {
              customPermission: requireFeatures(FEATURES.CRM),
              pageTitle: 'Oportunidades',
              search: {
                title: 'Configurações — CRM — Oportunidades',
                description: 'Etapas do funil de oportunidades',
                keywords: [
                  'funil de oportunidades',
                  'cadastrar funil de oportunidades',
                  'criar funil de oportunidades',
                  'etapas de oportunidades',
                  'configurar oportunidades',
                ],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/crm/view-crm-oportunidades/view-crm-oportunidades.vue'
                )
              ),
          },
          {
            path: 'motivos-perda',
            name: 'settings-v2.crm.motivos-perda',
            meta: {
              customPermission: requireFeatures(FEATURES.CRM),
              pageTitle: 'Motivos de Perda',
              search: {
                title: 'Configurações — CRM — Motivos de perda',
                description: 'Motivos de perda de oportunidades',
                keywords: [
                  'motivo de perda',
                  'motivos de perda',
                  'cadastrar motivo de perda',
                  'criar motivo de perda',
                  'adicionar motivo de perda',
                  'razão da perda',
                ],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@/src/modules/settings/views/crm/view-crm-motivos-perda/view-crm-motivos-perda.vue'
                )
              ),
          },
        ],
      },

      {
        path: '/configuracoes/atendimento',
        name: 'settings-v2.atendimento',
        meta: {
          customPermission: requireConexoesAtendimento,
          search: {
            title: 'Configurações — Mensagens',
            description: 'Configurações de mensagens e conexões WhatsApp',
            keywords: [
              'configurações',
              'atendimento',
              'mensagem',
              'mensagens',
              'whatsapp',
              'conexões',
            ],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        redirect: '/configuracoes/atendimento/',
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/atendimento/view-atendimento-settings.vue'
            )
          ),
        children: [
          {
            path: '/',
            name: 'settings-v2.atendimento.home',
            meta: {
              customPermission: requireConexoesAtendimento,
              pageTitle: 'Mensagens',
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/atendimento/view-atendimento-home.vue'
                )
              ),
          },
          {
            path: 'conexoes',
            name: 'settings-v2.atendimento.conexoes',
            meta: {
              customPermission: requireConexoesAtendimento,
              pageTitle: 'Conexões WhatsApp',
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/atendimento/view-atendimento-conexoes/view-atendimento-conexoes.vue'
                )
              ),
          },
        ],
      },

      {
        // Configurações do módulo Solicitações. Vive em Settings v2 (não como aba
        // do módulo) para herdar o gate IS_GESTOR + feature-flag: só o escritório
        // que tem a funcionalidade `Solicitações` habilitada (o interno) acessa.
        path: '/configuracoes/solicitacoes',
        name: 'settings-v2.solicitacoes',
        meta: {
          // flag + gestor + escritório interno (id_interno === 2976), mesma
          // lógica do isResponder da view — ver requireSolicitacoesInternal.
          customPermission: requireSolicitacoesInternal,
          pageTitle: 'Solicitações',
          search: {
            title: 'Configurações — Solicitações',
            description: 'Configurações do módulo de solicitações',
            keywords: ['configurações', 'solicitações', 'requisições'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/solicitacoes/view-solicitacoes-settings.vue'
            )
          ),
      },

      {
        path: '/configuracoes/comissoes',
        name: 'settings-v2.comissoes',
        meta: {
          customPermission: commissionsCheckPermission,
          search: {
            title: 'Configurações — Comissões',
            description: 'Configurações de comissões e grupos de pagamento',
            keywords: ['configurações', 'comissões', 'pagamentos', 'grupos'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        redirect: '/configuracoes/comissoes/',
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/view-comissoes/view-comissoes-settings.vue'
            )
          ),
        children: [
          {
            path: '/',
            name: 'settings-v2.comissoes.home',
            meta: {
              customPermission: commissionsCheckPermission,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/view-comissoes/view-comissoes-settings-home.vue'
                )
              ),
          },
          {
            path: '/configuracoes/comissoes/profissionais',
            name: 'settings-v2.comissoes.profissionais',
            meta: {
              // can_show: FEATURES.COMISSOES,
              pageTitle: 'Profissionais',
              customPermission: commissionsCheckPermission,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/view-comissoes/view-comissoes-professionals.vue'
                )
              ),
          },
          {
            path: '/configuracoes/comissoes/grupos_pagamento',
            name: 'settings-v2.comissoes.grupos_pagamentos',
            meta: {
              pageTitle: 'Grupos de Pagamentos',
              // can_show: FEATURES.COMISSOES,
              customPermission: commissionsCheckPermission,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/view-comissoes/view-comissoes-grupo-pagamento.vue'
                )
              ),
          },
          {
            path: '/configuracoes/comissoes/parametros',
            name: 'settings-v2.comissoes.parametros',
            meta: {
              pageTitle: 'Parâmetros',
              customPermission: commissionsCheckPermission,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/view-comissoes/office-parameters.vue'
                )
              ),
          },
          {
            path: '/configuracoes/comissoes/filiais',
            name: 'settings-v2.comissoes.filiais',
            meta: {
              pageTitle: 'Filiais',
              customPermission: commissionsCheckPermission,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/view-comissoes/view-comissoes-filials.vue'
                )
              ),
          },
          {
            path: '/configuracoes/comissoes/areas',
            name: 'settings-v2.comissoes.areas',
            meta: {
              pageTitle: 'Áreas de produto',
              customPermission: commissionsCheckPermission,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/view-comissoes/view-comissoes-areas.vue'
                )
              ),
          },
          {
            path: '/configuracoes/comissoes/teams',
            name: 'settings-v2.comissoes.teams',
            meta: {
              pageTitle: 'Equipes',
              customPermission: commissionsCheckPermission,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/view-comissoes/view-comissoes-teams.vue'
                )
              ),
          },
        ],
      },
    ],
  },
  {
    path: '/configuracoes/configuracoes-geral',
    name: 'settings-v2.general',
    meta: {
      customPermission: requireGestor,
      search: {
        title: 'Configurações — Gerais',
        description: 'Configurações gerais, profissionais e parâmetros',
        keywords: ['configurações', 'gerais', 'parâmetros', 'profissionais'],
        group: SEARCH_GROUPS.CONFIGURACOES,
      },
    },
    redirect: '/configuracoes/configuracoes-geral',
    component: () =>
      lazyLoadView(
        import('@src/modules/settings/views/general/view-modulo-general.vue')
      ),
    children: [
      {
        path: '/',
        name: 'settings-v2.general.home',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import('@src/modules/settings/views/general/view-general-home.vue')
          ),
      },
      {
        path: 'profissionais',
        name: 'settings-v2.general.profissionais',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import(
              '@src/modules/settings/views/general/view-general-profissionais.vue'
            )
          ),
      },
      {
        path: 'parametros',
        name: 'settings-v2.general.parameters_office',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import(
              '@src/modules/settings/views/general/view-general-parameters-office.vue'
            )
          ),
      },
      {
        path: 'permissoes',
        name: 'settings-v2.general.permissions',
        meta: {
          customPermission: requireGestor,
          search: {
            title: 'Permissões de Acesso dos Profissionais',
            description:
              'Configure as funcionalidades que cada profissional pode acessar',
            keywords: [
              // Singular e plural: 'permissões' não contém 'permissão' no
              // matcher includes() (o plural muda o radical ão→ões).
              'permissão',
              'permissões',
              'acessos',
              'profissionais',
              'funcionalidades',
              'features',
              'flags',
              'liberar acesso',
            ],
            icon: 'key-left',
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@src/modules/settings/views/general/view-permissions-access-profissionals-general.vue'
            )
          ),
      },
      {
        path: 'cnpj-filiais',
        name: 'settings-v2.general.cnpjs_filials',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import(
              '@src/modules/settings/views/general/view-general-cnpjs-filials.vue'
            )
          ),
      },

      {
        path: 'cnpj-filiais/cnpjs',
        name: 'settings-v2.general.cnpjs_filials.cnpjs',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import('@src/modules/settings/views/general/view-general-cnpjs.vue')
          ),
      },

      {
        path: 'cnpj-filiais/filiais',
        name: 'settings-v2.general.cnpjs_filials.filiais',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import(
              '@src/modules/settings/views/general/view-general-filials.vue'
            )
          ),
      },

      {
        path: 'cnpj-filiais/contas-correntes',
        name: 'settings-v2.general.cnpjs_filials.contas_correntes',
        meta: {
          can_show: FEATURES.OPEN_FINANCE_PJ,
          customPermission: requireFeatures(FEATURES.OPEN_FINANCE_PJ),
        },
        component: () =>
          lazyLoadView(
            import(
              '@src/modules/settings/views/general/view-general-contas-correntes.vue'
            )
          ),
      },

      {
        path: 'equipes',
        name: 'settings-v2.general.teams',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import('@src/modules/settings/views/general/view-general-teams.vue')
          ),
      },

      {
        path: 'acesso-espelhado',
        name: 'settings-v2.general.mirrored-access',
        meta: {
          customPermission: requireGestor,
        },
        component: () =>
          lazyLoadView(
            import(
              '@src/modules/settings/views/general/view-general-mirrored-access.vue'
            )
          ),
      },
    ],
  },

  {
    path: '/configuracoes/asset-allocation/',
    name: 'settings-v2.asset_allocation',
    meta: {
      customPermission: requireBaseAtivos,
      search: {
        title: 'Configurações — Asset Allocation',
        description: 'Configurações de asset allocation e carteiras',
        keywords: [
          'configurações',
          'asset allocation',
          'carteiras',
          'benchmarks',
        ],
        group: SEARCH_GROUPS.CONFIGURACOES,
      },
    },
    redirect: '/configuracoes/asset-allocation',
    component: () =>
      lazyLoadView(
        import(
          '@/src/modules/settings/views/asset-allocation/view-asset-allocation-modulo.vue'
        )
      ),
    children: [
      {
        path: '/',
        name: 'settings-v2.asset_allocation.home',
        meta: {
          customPermission: requireBaseAtivos,
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-home.vue'
            )
          ),
      },
      {
        path: 'assets',
        name: ASSETS_ROUTE_NAME,
        meta: {
          customPermission: requireBaseAtivos,
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-assets.vue'
            )
          ),
      },
      {
        path: 'profile',
        name: 'settings-v2.asset_allocation.profile',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-profile.vue'
            )
          ),
      },

      {
        path: 'benchmarks',
        name: 'settings-v2.asset_allocation.benchmarks',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-benchmarks.vue'
            )
          ),
      },
      {
        path: 'billing-models',
        name: 'settings-v2.asset_allocation.billing_models',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-billing-models.vue'
            )
          ),
      },
      {
        path: 'email-customization',
        name: 'settings-v2.asset_allocation.email_customization',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-email-customization.vue'
            )
          ),
      },
      {
        path: 'strategic-classes',
        name: 'settings-v2.asset_allocation.strategic_classes',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-strategic-classes.vue'
            )
          ),
      },
      {
        path: 'report-customization',
        name: 'settings-v2.asset_allocation.report_customization',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-report-customization.vue'
            )
          ),
      },
      {
        path: 'portfolios',
        name: 'settings-v2.asset_allocation.portfolios',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-portfolios.vue'
            )
          ),
      },
      {
        path: 'segmentation',
        name: 'settings-v2.asset_allocation.customer_segmentation',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-customer-segmentation.vue'
            )
          ),
      },
      {
        path: 'segmentation/faixa-pl',
        name: 'settings-v2.asset_allocation.segmentation_customers.pl',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-customer-segmentation-pl'
            )
          ),
      },
      {
        path: 'segmentation/grupo-familiar',
        name: 'settings-v2.asset_allocation.segmentation_customers.family_group',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-customer-segmentation-family-group'
            )
          ),
      },
      {
        path: 'sheets/xp',
        name: 'settings-v2.asset_allocation.sheets.xp',
        meta: {
          can_show: FEATURES.XP_WORKSHEETS && FEATURES.ASSET_LOCATION,
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-sheets-xp.vue'
            )
          ),
      },
      {
        path: 'sheets/btg',
        name: 'settings-v2.asset_allocation.sheets.btg',
        meta: {
          can_show: FEATURES.BTG_WORKSHEETS && FEATURES.ASSET_LOCATION,
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-sheets-btg.vue'
            )
          ),
      },
      {
        path: 'permissoes',
        name: 'settings-v2.asset_allocation.permissoes',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-permissoes.vue'
            )
          ),
      },
      {
        path: 'permissoes/assessores',
        name: 'settings-v2.asset_allocation.permissoes.assessores',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-permissoes-assessores'
            )
          ),
      },
      {
        path: 'permissoes/clientes',
        name: 'settings-v2.asset_allocation.permissoes.clientes',
        meta: {
          customPermission: requireFeatures(FEATURES.ASSET_LOCATION),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/permissoes-clientes.vue'
            )
          ),
      },
      {
        path: 'permissoes/bi',
        name: 'settings-v2.asset_allocation.permissoes.bi',
        meta: {
          can_show: FEATURES.ASSET_LOCATION && FEATURES.BI_CUSTOMIZED,
          permission: ['gestor'],
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-permissoes-bi.vue'
            )
          ),
      },
    ],
  },

  {
    path: '/configuracoes/financial-planning',
    name: 'settings-v2.financial-planning',
    meta: {
      customPermission: requireFeatures(FEATURES.FINANCIAL_PLANNING_SIMULATOR),
      search: {
        title: 'Configurações — Financial Planning',
        description: 'Configurações de financial planning e relatório',
        keywords: [
          'configurações',
          'financial planning',
          'relatório',
          'planejamento',
        ],
        group: SEARCH_GROUPS.CONFIGURACOES,
      },
    },
    redirect: { name: 'settings-v2.financial-planning.index' },
    component: () =>
      lazyLoadView(
        import(
          '@modules/settings/views/view-financial-planning-settings/financial-planning-settings.vue'
        )
      ),
    children: [
      {
        path: '/',
        name: 'settings-v2.financial-planning.index',
        meta: {
          customPermission: requireFeatures(
            FEATURES.FINANCIAL_PLANNING_SIMULATOR
          ),
        },
        component: () =>
          lazyLoadView(
            import('@modules/financial-planning/views/view-configuracoes.vue')
          ),
      },
      {
        // Visão de adoção: conexões Open Finance de todos os clientes do
        // escritório. `requireFeatures` já embute IS_GESTOR — a rota inteira de
        // Configurações é gestor-only —, e o backend repete o gate com
        // decorator_check_is_manager.
        path: 'open-finance',
        name: 'settings-v2.financial-planning.open-finance',
        meta: {
          // `can_show` ALEM do `customPermission`, em paridade com a tela de
          // conexoes do proprio modulo (`fp.contas`): o guard aplica `can_show`
          // a QUALQUER perfil, antes e independentemente do customPermission
          // (`router/index.js:307-330`), e e ele que a busca global
          // (`global-search/search-index.js`) e o catalogo de Configuracoes
          // leem. Sem ele a rota some da tela mas continua indexavel.
          can_show: FEATURES.FINANCIAL_PLANNING_SIMULATOR,
          customPermission: requireFeatures(
            FEATURES.FINANCIAL_PLANNING_SIMULATOR
          ),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/financial-planning/views/view-fp-open-finance-conexoes/view-fp-open-finance-conexoes.vue'
            )
          ),
      },
      {
        path: 'modelo-relatorio',
        name: 'settings-v2.financial-planning.modelo-relatorio',
        meta: {
          customPermission: (user) => {
            return permissionSettingsV2(user, true, [
              FEATURES.FINANCIAL_PLANNING_SIMULATOR,
            ])
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/view-financial-planning-settings/view-fp-report-config.vue'
            )
          ),
      },
    ],
  },

  {
    path: '/configuracoes/gestao-desempenho/',
    name: 'settings-v2.rh',
    meta: {
      customPermission: requireFeatures(FEATURES.RH),
      search: {
        title: 'Configurações — Gestão de Desempenho',
        description: 'Configurações de gestão de desempenho e avaliação',
        keywords: ['configurações', 'gestão de desempenho', 'rh', 'avaliação'],
        group: SEARCH_GROUPS.CONFIGURACOES,
      },
    },
    redirect: '/configuracoes/gestao-desempenho',
    component: () =>
      lazyLoadView(
        import('@/src/modules/settings/views/view-rh-settings/rh-settings.vue')
      ),
    children: [
      {
        path: '/',
        name: 'settings-v2.rh.home',
        meta: {
          customPermission: requireFeatures(FEATURES.RH),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/view-rh-home-settings/rh-home-settings.vue'
            )
          ),
      },
      {
        path: 'formulario-avaliacao',
        name: 'settings-v2.rh.form',
        meta: {
          customPermission: requireFeatures(FEATURES.RH),
          search: {
            title:
              'Configurações — Gestão de Desempenho — Formulários de Avaliação',
            description: 'Perguntas usadas nas avaliações de desempenho',
            keywords: ['formulário', 'avaliação', 'perguntas', 'desempenho'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/rh/views/form-validation-rh/view-form-validation-rh.vue'
            )
          ),
      },
      {
        path: 'indicadores',
        name: 'settings-v2.rh.indicators',
        meta: {
          customPermission: requireFeatures(FEATURES.RH),
          search: {
            title: 'Configurações — Gestão de Desempenho — Indicadores',
            description: 'Indicadores usados na gestão de desempenho',
            keywords: ['indicadores', 'metas', 'kpi', 'desempenho'],
            group: SEARCH_GROUPS.CONFIGURACOES,
            actions: [
              {
                id: 'criar-indicador',
                title: 'Criar indicador de desempenho',
                keywords: ['indicador', 'novo indicador', 'meta', 'kpi'],
                to: { name: 'settings-v2.rh.indicators' },
                // view-indicators.vue monta o modal; open({ item = {} } = {})
                // aceita emissão sem payload (cadastro novo).
                modal: MODAL.MODAL_RH_CREATE_INDICATOR,
              },
            ],
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/rh/views/indicators/view-indicators.vue')
          ),
      },
      {
        path: 'equipes',
        name: 'settings-v2.rh.teams',
        meta: {
          customPermission: requireFeatures(FEATURES.RH),
          search: {
            title: 'Configurações — Gestão de Desempenho — Equipes',
            description: 'Equipes usadas na gestão de desempenho',
            keywords: ['equipes', 'times', 'desempenho', 'rh'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        component: () =>
          lazyLoadView(import('@modules/settings/views/teams/view-teams.vue')),
      },
      {
        path: 'permissoes',
        name: 'settings-v2.rh.permissions',
        meta: {
          customPermission: requireFeatures(FEATURES.RH),
          search: {
            title: 'Configurações — Gestão de Desempenho — Permissões',
            description: 'Quem acessa e participa das avaliações de desempenho',
            keywords: ['permissões', 'acesso', 'avaliação', 'desempenho'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/rh-permissions/view-rh-permissions.vue'
            )
          ),
      },
      {
        path: 'filiais',
        name: 'settings-v2.rh.filiais',
        meta: {
          customPermission: requireFeatures(FEATURES.RH),
          search: {
            title: 'Configurações — Gestão de Desempenho — Filiais',
            description: 'Filiais usadas na gestão de desempenho',
            keywords: ['filiais', 'filial', 'unidades', 'desempenho'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/view-comissoes/view-comissoes-filials.vue'
            )
          ),
      },
      {
        path: 'comercial_1_1',
        name: 'settings-v2.rh.comercial_1_1',
        meta: {
          customPermission: requireFeatures(FEATURES.RH),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/comercial-1-1/view-comercial-1-1.vue'
            )
          ),
      },
    ],
  },
  {
    path: '/configuracoes/compliance/',
    name: 'settings-v2.compliance',
    meta: {
      customPermission: requireFeatures(FEATURES.COMPLIANCE),
      search: {
        title: 'Configurações — Compliance',
        description: 'Configurações de compliance, documentos e IPS',
        keywords: ['configurações', 'compliance', 'documentos', 'ips'],
        group: SEARCH_GROUPS.CONFIGURACOES,
      },
    },
    redirect: '/configuracoes/compliance/',
    component: () =>
      lazyLoadView(
        import('@/src/modules/settings/views/view-compliance/complice-home.vue')
      ),
    children: [
      {
        path: '/',
        name: 'settings-v2.compliance.home',
        meta: {
          customPermission: requireFeatures(FEATURES.COMPLIANCE),
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/view-compliance/compliance-options/compliance-options.vue'
            )
          ),
      },
      {
        path: 'kit-documentos',
        name: 'settings-v2.compliance.kit-partners',
        meta: {
          customPermission: requireFeatures(FEATURES.COMPLIANCE),
        },
        component: () =>
          lazyLoadView(
            import(
              '@/src/modules/settings/views/juridico/settings-kit-partners/settings-kit-partners.vue'
            )
          ),
      },
      {
        path: 'tipos',
        name: 'settings-v2.compliance.tipos',
        meta: {
          customPermission: requireFeatures(FEATURES.COMPLIANCE),
          search: {
            title: 'Configurações — Compliance — Tipos de Documentos',
            description: 'Tipos de documentos do Compliance',
            keywords: ['compliance', 'tipos', 'documentos'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@/src/modules/settings/views/type-documents/view-type-documents.vue'
            )
          ),
      },
      {
        path: 'ips',
        name: 'settings-v2.compliance.ips',
        meta: {
          customPermission: requireFeatures(FEATURES.COMPLIANCE),
          search: {
            title: 'Configurações — Compliance — IPS',
            description: 'Perguntas do diagnóstico IPS com os clientes',
            keywords: ['ips', 'investment policy statement', 'perguntas'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        component: () =>
          lazyLoadView(import('@views/settings/ips/configurar-perguntas')),
      },
      {
        path: 'profile',
        name: 'settings-v2.compliance.profile',
        meta: {
          customPermission: requireFeatures(FEATURES.COMPLIANCE),
          search: {
            title: 'Configurações — Compliance — Perfil de Investidor',
            description: 'Perfis de investidor e segmentação de clientes',
            keywords: ['perfil', 'investidor', 'suitability', 'compliance'],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/settings/views/asset-allocation/view-asset-allocation-profile.vue'
            )
          ),
      },
    ],
  },
]
